import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Bot, ShieldCheck, Mail, RefreshCcw } from 'lucide-react';
import Footer from './Footer';
import { HOMEPAGE_ROUTE } from '../data/siteConfig.js';

const resources = [
  {
    title: 'Sitemap XML',
    description: 'Canonical URL inventory for crawl discovery and change detection.',
    href: '/sitemap.xml',
    external: true,
    icon: FileText,
  },
  {
    title: 'LLMs TXT',
    description: 'Compact machine-readable guide for AI systems and answer engines.',
    href: '/llms.txt',
    external: true,
    icon: Bot,
  },
  {
    title: 'LLMs Full TXT',
    description: 'Expanded machine-readable context with more detailed Touchstone guidance.',
    href: '/llms-full.txt',
    external: true,
    icon: Bot,
  },
  {
    title: 'Editorial Policy',
    description: 'How Touchstone handles neutrality, corrections, and evidence standards.',
    href: '/editorial-policy',
    external: false,
    icon: ShieldCheck,
  },
  {
    title: 'Contact',
    description: 'Reach Touchstone for corrections, questions, disputes, or partnerships.',
    href: '/contact',
    external: false,
    icon: Mail,
  },
];

export default function MachineReadableResourcesPage() {
  const navigate = useNavigate();

  return (
    <div className="fixed inset-0 overflow-y-auto bg-[#030303] select-text">
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#030303]/80 backdrop-blur-md border-b border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link
            to={HOMEPAGE_ROUTE}
            className="text-xl text-white"
            style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif", letterSpacing: '0.05em' }}
          >
            TOUCHSTONE
          </Link>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </header>

      <div className="pt-36 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h1
            className="text-4xl md:text-5xl text-white mb-4"
            style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif" }}
          >
            Machine-Readable Resources
          </h1>
          <p className="text-base text-zinc-500 mb-12 max-w-2xl">
            Central index for Touchstone resources that help crawlers, answer engines, and human reviewers
            understand what we publish, how we maintain it, and where to send corrections.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
            {resources.map((resource) => {
              const Icon = resource.icon;

              return (
                <div key={resource.title} className="p-6 rounded-2xl border border-white/[0.06] bg-white/[0.02]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-zinc-400" />
                    </div>
                    <h2 className="text-lg text-white font-medium">{resource.title}</h2>
                  </div>
                  <p className="text-zinc-400 text-sm mb-4">{resource.description}</p>
                  {resource.external ? (
                    <a href={resource.href} className="text-white hover:underline text-sm">
                      Open resource
                    </a>
                  ) : (
                    <Link to={resource.href} className="text-white hover:underline text-sm">
                      Open page
                    </Link>
                  )}
                </div>
              );
            })}
          </div>

          <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 mb-10">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center">
                <RefreshCcw className="w-5 h-5 text-zinc-400" />
              </div>
              <h2 className="text-2xl text-white font-medium">Update Methodology</h2>
            </div>
            <div className="space-y-4 text-sm text-zinc-400 leading-relaxed">
              <p>
                Touchstone updates high-value pages when vendor pricing, capability details, documentation, or
                policy claims materially change.
              </p>
              <p>
                Our standard review cycle checks official documentation first, then pricing pages, then release
                notes or changelogs when available. When information is unclear, we label uncertainty instead of
                filling gaps with assumptions.
              </p>
              <p>
                Correction requests and vendor disputes are routed through the contact and editorial channels
                linked above, then reflected in page-level verification dates once reviewed.
              </p>
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}
